import { TherapyDayData } from '@lmt/signal-viewer-ui';
import {
  createEdfManifest,
  createP5EdfManifest,
  type ArchiveEdfManifest,
  type TherapyGeneration,
} from '@lmt/signal-viewer-processor';
import {
  FileUploadData,
  FileUploadValidationError,
} from './file-upload';
import { PredefinedProfileIds } from './profiler-helper';
import { resolveCustomEvents } from './resolve-events';

export type NormalizedUpload = {
  therapyData: TherapyDayData;
  edfManifest: ArchiveEdfManifest;
  profileSwitchId: string;
  headerGeneration: TherapyGeneration | null;
};

const HEADER_GENERATION_BY_CONTAINER: Record<
  FileUploadData['containerType'],
  TherapyGeneration | null
> = {
  psdat: 'p28',
  pdat: 'p3',
  pvdat: 'p32',
  pldat: 'p5',
  custom: null,
};

const GENERATION_BY_CONTAINER: Record<
  FileUploadData['containerType'],
  TherapyGeneration
> = {
  psdat: 'p28',
  pdat: 'p3',
  pvdat: 'p32',
  pldat: 'p5',
  custom: 'p28',
};

const PROFILE_BY_CONTAINER: Record<FileUploadData['containerType'], string> = {
  psdat: PredefinedProfileIds.P28,
  pdat: PredefinedProfileIds.P3,
  pvdat: PredefinedProfileIds.P32,
  pldat: PredefinedProfileIds.P5,
  custom: PredefinedProfileIds.P28,
};

export const normalizeUpload = async (
  uploadedData: FileUploadData,
): Promise<NormalizedUpload> => {
  const { containerType } = uploadedData;
  const edfManifest =
    containerType === 'custom'
      ? []
      : await buildEdfManifest(uploadedData);

  // Container events come from the backend after normalization (see
  // getTherapyArchiveData / getBackendEvents in events-helper), hence the
  // empty list for non-custom uploads.
  const events =
    containerType === 'custom'
      ? await resolveCustomEvents(
          uploadedData.edfFiles.map((edfFile, idx) => ({
            edfFile,
            eventXml: uploadedData.eventXmls?.[idx],
          })),
        )
      : [];

  return {
    therapyData: {
      signals: uploadedData.edfFiles,
      events,
      generation: GENERATION_BY_CONTAINER[containerType],
    },
    edfManifest,
    profileSwitchId: PROFILE_BY_CONTAINER[containerType],
    headerGeneration: HEADER_GENERATION_BY_CONTAINER[containerType],
  };
};

const buildEdfManifest = async (
  uploadedData: FileUploadData,
): Promise<ArchiveEdfManifest> => {
  const sources = uploadedData.edfFiles.map((file, index) => ({
    file,
    path: uploadedData.edfArchivePaths?.[index] ?? file.name,
  }));

  // Löwenstein P5 is the only generation whose MCC event API needs the
  // device-clock value stored in EDF's local-recording-identification field.
  const result =
    uploadedData.containerType === 'pldat'
      ? await createP5EdfManifest(sources)
      : await createEdfManifest(sources);

  if (!result.isSuccess) {
    throw new FileUploadValidationError(
      'corrupted-file',
      `Failed to read EDF metadata from ${result.path}: ${result.error}`,
      [result.path],
    );
  }

  return result.data;
};
